import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { cloneDeep } from 'lodash';
// eslint-disable-next-line import/no-unresolved
import { csSyncDataSubject$, SyncDataSubject } from '@creditsights/cs-web';
import { Preference } from '../../models/Preference';
import { updateMyContentPreferences } from '../../services/myContentPreferences/myContentPreferences.service';
import { Channels } from '../../utils/channels';
import { queryKeys } from '../queryKeyFactory';
import { invalidateFeedQueries } from '../../utils/invalidateFeedQueries';
import useMutationErrorToast from '../toast/useMutationErrorToast';
import { ApiError } from '../../services/api-error.model';

export default (): UseMutationResult<void, ApiError, Preference[], { previousPreferences: Preference[] }> => {
  const queryClient = useQueryClient();
  const { showErrorToast } = useMutationErrorToast();

  return useMutation(
    async (myContentPreferences: Preference[]) => {
      await updateMyContentPreferences(myContentPreferences);
    },
    {
      onMutate: async (preferences: Preference[]) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries([queryKeys.fetchMyContentPreferences]);

        const previousPreferences = cloneDeep(
          queryClient.getQueryData<Preference[]>([queryKeys.fetchMyContentPreferences])!
        );

        queryClient.setQueryData([queryKeys.fetchMyContentPreferences], preferences);

        return { previousPreferences };
      },
      onError: (error, _preferences, context) => {
        queryClient.setQueryData([queryKeys.fetchMyContentPreferences], context?.previousPreferences);
        showErrorToast(error);
      },
      onSuccess: async (_, preferences) => {
        invalidateFeedQueries(queryClient);
        await Channels.preferencesChannel.postMessage(preferences);
        csSyncDataSubject$.next(SyncDataSubject.USER_PREFERENCE);
      },
    }
  );
};
